import CanteenRatingBanner from '@/components/CanteenRatingBanner'
import type { FragmentType } from '@/generated/gql/fragment-masking'
import { useFragment } from '@/generated/gql/fragment-masking'
import { graphql } from '@/generated/gql/gql'
import { Meal } from '@/generated/gql/graphql'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import { useLazyQuery } from '@apollo/client'
import HourglassTop from '@mui/icons-material/HourglassTop'
import Info from '@mui/icons-material/Info'
import {
  Dialog,
  Stack,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material'

import { useState, useEffect, useMemo, useRef } from 'react'

import FiltersPanelMuiV2 from './FiltersPanelV2/FiltersPanelMuiV2'
import type { BudgetFilter, BudgetFilterValue, FilterCategory } from './FiltersPanelV2/types'
import { ItemRatingV2 } from './ItemRatingV2/index'
import { MenuBody } from './MenuBody/index'
import { useCartState } from './MenuCart/useCartState'
import { MenuCartV2 } from './MenuCartV2/index'
import { MenuHeaderV2 } from './MenuHeaderV2/index'
import Navbar from './Navbar'
import { serializeMenuQueryDate } from './state/menuQueryDates'
import type { CcAppPromo } from './state/useCcAppPromo'
import { usePlaceCanteenOrder } from './state/usePlaceCanteenOrder'

const canteenMenuBodyCurrentMenuItemsQueryV2 = graphql(`
  query CanteenMenuBody_CurrentMenuItemsQueryV2(
    $meal: Meal!
    $date: DateTime!
    $timeslotStart: DateTime!
    $mainsFilters: CCMenuItemFilters!
    $sidesFilters: CCMenuItemFilters!
    $drinksFilters: CCMenuItemFilters!
    $vendorFilters: CCVendorMenusQueryInput
  ) {
    me {
      id
      ccMenu(input: { meal: $meal, date: $date, timeslotStart: $timeslotStart }) {
        id
        mealServiceData {
          id
          availableSubsidy
          ...CanteenOrderedItems_MenuDataFragment
          timeslots {
            id
            ordersOpenTime
            isPreOrdersOpenTime
            ordersAreOpen
            ordersCloseTime
            isPostOrdersCloseTime
            id
            allVendorMenus: vendorMenus {
              id
              ...CanteenMenuFiltersPanel_VendorMenuDataFragmentV2
            }
            filteredVendorMenus: vendorMenus(input: $vendorFilters) {
              id
              vendor {
                id
                ...CanteenMenuItems_VendorDataFragment
              }
              mains: menuItems(limit: 200, filters: $mainsFilters) {
                items {
                  id
                  liveMenuItem {
                    _id
                    supplierId
                    dietaries
                    allergens
                    subAllergens {
                      nuts
                      cereals
                    }
                  }
                  liveVariation {
                    priceCustomerFacing
                  }
                  minimumPurchasablePrice
                  ...CanteenMenuItems_MenuItemDataFragment
                }
              }
              sides: menuItems(limit: 200, filters: $sidesFilters) {
                items {
                  id
                  liveMenuItem {
                    _id
                    supplierId
                    dietaries
                    allergens
                    subAllergens {
                      nuts
                      cereals
                    }
                  }
                  liveVariation {
                    priceCustomerFacing
                  }
                  minimumPurchasablePrice
                  ...CanteenMenuItems_MenuItemDataFragment
                }
              }
              drinks: menuItems(limit: 200, filters: $drinksFilters) {
                items {
                  id
                  liveMenuItem {
                    _id
                    supplierId
                    dietaries
                    allergens
                    subAllergens {
                      nuts
                      cereals
                    }
                  }
                  liveVariation {
                    priceCustomerFacing
                  }
                  minimumPurchasablePrice
                  ...CanteenMenuItems_MenuItemDataFragment
                }
              }
            }
          }
        }
      }
    }
  }
`)

const canteenMenuViewCurrentMenuDataFragment = graphql(`
  fragment CanteenMenuView_CurrentMenuDataFragment on CCMealService {
    ...CanteenMenuHeader_MenuDataFragmentV2
    ...CanteenMenuCart_MenuDataFragment
    ...CanteenMenuCartV2_MenuDataFragment
    ...CanteenMenuHeader_CurrentMenuDataFragment
    availableSubsidy
    openTime
  }
`)

const canteenMenuViewUserDataFragment = graphql(`
  fragment CanteenMenuView_UserDataFragmentV2 on User {
    ...CanteenMenuHeader_UserDataFragmentV2
    ...CanteenMenuHeader_UserDataFragment
    ...CanteenMenuCart_UserDataFragment
    ...CanteenMenuCartV2_UserDataFragment
    ...CanteenNavbar_UserDataFragment
    accountLocation {
      id
      timezone
    }
    subsidySettings {
      unlimitedSubsidy
    }
    wallet {
      balance
    }
    account {
      id
      userInterfaceSettings {
        hideCCPrices
      }
    }
  }
`)

const FILTER_MAP = {
  // DIETARIES
  vegan: 'vegan',
  vegetarian: 'vegetarian',
  kosher: 'kosher',
  halal: 'halal',
  plantpacked: 'powerplant',
  lowcarb: 'lowcarb',
  highprotein: 'highprotein',
  norefinedsugar: 'refined_sugar',
  lowcarbon: 'low_carbon',
  verylowcarbon: 'verylow_carbon',
  pescatarian: 'pescatarian',
  nonhfss: 'non_hfss',

  // ALLERGENS
  cereals: 'cereals',
  crustaceans: 'crustaceans',
  fish: 'fish',
  peanuts: 'peanuts',
  soybeans: 'soybeans',
  milk: 'milk',
  nuts: 'nuts',
  celery: 'celery',
  mustard: 'mustard',
  sesame: 'sesame',
  sulphur: 'sulphur',
  lupin: 'lupin',
  molluscs: 'molluscs',
  eggs: 'eggs',
  none: 'none',
} as const

const ALLERGEN_KEYS = new Set([
  'cereals',
  'crustaceans',
  'fish',
  'peanuts',
  'soybeans',
  'milk',
  'nuts',
  'celery',
  'mustard',
  'sesame',
  'sulphur',
  'lupin',
  'molluscs',
  'eggs',
  'none',
])

interface MenuViewV2Props {
  meal: string
  date: string
  timeslot?: string | null
  currency: string
  userId: string
  userData: FragmentType<typeof canteenMenuViewUserDataFragment>
  appPromo?: CcAppPromo | null
  menuData?: FragmentType<typeof canteenMenuViewCurrentMenuDataFragment> | null
  menuFilters: { dietaries: string[]; allergens: string[]; vendors: string[] }
  searchTerm: string
  menuClosed: boolean
  language: string
  orderTimeRemaining: number
  loadingSelectedServiceData: boolean
  budgetFilter: BudgetFilter
  onToggleBudgetFilter: (value: BudgetFilterValue) => void
  onClearFilters: () => void
  onSearchTermChange: (value: string) => void
  onToggleFilterValue: (category: FilterCategory, value: string) => void
  onChangeSelectedService: (input: { meal: string; date: string }) => void
  onChangeAccountLocation: (accountLocationId: number) => void
  onOrderPlacement: (data: { success: boolean; forwardUrl: string }) => void
  reloadFromApi: () => Promise<void>
  onChangeTimeslot: (timeslot: string) => void
}

export function MenuViewV2({
  date,
  meal,
  timeslot,
  language,
  userId,
  userData: userDataProp,
  appPromo,
  menuData: menuDataProp,
  menuClosed,
  orderTimeRemaining,
  currency,
  menuFilters,
  searchTerm,
  loadingSelectedServiceData,
  budgetFilter,
  onToggleBudgetFilter,
  onClearFilters,
  onSearchTermChange,
  onOrderPlacement,
  reloadFromApi,
  onChangeSelectedService,
  onChangeAccountLocation,
  onChangeTimeslot,
  onToggleFilterValue,
}: MenuViewV2Props) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const dietariesFiltersString = menuFilters.dietaries.join(',')
  const allergensFiltersString = menuFilters.allergens.join(',')
  const vendorFiltersString = menuFilters.vendors.join(',')

  const userData = useFragment(canteenMenuViewUserDataFragment, userDataProp)
  const timezone = userData.accountLocation?.timezone
  const dateISOString = serializeMenuQueryDate(date, timezone)
  // Match Moment's existing absent-value behavior: undefined means now, null is invalid.
  const timeslotISOString =
    timeslot === undefined
      ? new Date().toISOString()
      : serializeMenuQueryDate(timeslot ?? '', timezone)
  const {
    wallet: { balance },
    subsidySettings: { unlimitedSubsidy },
  } = userData

  const menuData = useFragment(canteenMenuViewCurrentMenuDataFragment, menuDataProp)

  const hidePrices = Boolean(userData.account?.userInterfaceSettings?.hideCCPrices)

  // The canteen-orders banner replaces this prompt.
  const ccItemRatingEnabled = useFeatureFlag(SPLITS.CC_ITEM_RATING)
  const useSynchronousOrderPlacement = useFeatureFlag(SPLITS.USE_SYNCHRONOUS_CC_ORDER_PLACEMENT, {
    accountId: userData.account?.id ?? '',
  })
  const {
    addCartItem,
    updateCartItem,
    removeCartItem,
    emptyCart,
    cartItems,
    cartTotal,
    itemQuantity: cartItemQuantity,
  } = useCartState()
  const {
    placeOrder,
    reset: resetPlaceOrder,
    error: placeOrderError,
    data: placeOrderData,
    loading: placingOrder,
  } = usePlaceCanteenOrder({ useSynchronousOrderPlacement })

  const [runQuery, { data: menuDataV2, loading: loadingV2, previousData: previousMenuDataV2 }] =
    useLazyQuery(canteenMenuBodyCurrentMenuItemsQueryV2)

  const filteredMenuFilterCount = useMemo(() => {
    const filteredMenuData =
      menuDataV2?.me.ccMenu?.mealServiceData.timeslots[0]?.filteredVendorMenus ||
      previousMenuDataV2?.me.ccMenu?.mealServiceData.timeslots[0]?.filteredVendorMenus ||
      []

    const validMenus = menuFilters.vendors.length
      ? filteredMenuData.filter((menu) => menuFilters.vendors.includes(menu.vendor.id))
      : filteredMenuData

    let allItems = validMenus.flatMap((menu) => [
      ...menu.mains.items,
      ...menu.sides.items,
      ...menu.drinks.items,
    ])

    const remainingSubsidy = Math.round((menuData?.availableSubsidy ?? 0) * 100)
    const remainingBalance = Math.round((balance ?? 0) * 100)
    const remainingSubsidyAndCredits = remainingSubsidy + remainingBalance

    const withinSubsidyCount = unlimitedSubsidy
      ? allItems.length
      : allItems.filter((item) => (item.minimumPurchasablePrice ?? 0) <= remainingSubsidy).length
    const withinSubsidyAndCreditsCount = unlimitedSubsidy
      ? allItems.length
      : allItems.filter((item) => (item.minimumPurchasablePrice ?? 0) <= remainingSubsidyAndCredits)
          .length

    if (!unlimitedSubsidy && budgetFilter) {
      const allowance = budgetFilter === 'subsidy' ? remainingSubsidy : remainingSubsidyAndCredits
      allItems = allItems.filter((item) => (item.minimumPurchasablePrice ?? 0) <= allowance)
    }

    const totalItemCount = allItems.length

    const filterCounts: Record<string, any> = {}

    Object.values(FILTER_MAP).forEach((key) => {
      filterCounts[key] = 0
    })

    // Allergens start at total as they are listed as "No added [e.g. dairy]"
    Object.entries(FILTER_MAP).forEach(([raw, mapped]) => {
      if (ALLERGEN_KEYS.has(raw)) {
        filterCounts[mapped] = totalItemCount
      }
    })

    const vendorCounts: Record<number, number> = {}

    const activeAllergenFilters = (menuFilters.allergens || []).map((a) => a.toLowerCase())

    allItems.forEach((item) => {
      const { allergens, dietaries, subAllergens, supplierId } = item.liveMenuItem

      const itemDietaries = dietaries.map((d) => d.toLowerCase())

      const itemAllergens = [
        ...allergens,
        ...(subAllergens.cereals ?? []),
        ...(subAllergens.nuts ?? []),
      ].map((a) => a.toLowerCase())

      itemDietaries.forEach((d) => {
        const mapped = FILTER_MAP[d as keyof typeof FILTER_MAP]
        if (mapped) filterCounts[mapped]++
      })

      itemAllergens.forEach((a) => {
        const mapped = FILTER_MAP[a as keyof typeof FILTER_MAP]
        if (mapped) filterCounts[mapped]--
      })

      const hasBlockedAllergen = activeAllergenFilters.some((filter) =>
        itemAllergens.includes(filter),
      )

      if (!hasBlockedAllergen) {
        if (!vendorCounts[supplierId]) {
          vendorCounts[supplierId] = 0
        }
        vendorCounts[supplierId]++
      }
    })

    filterCounts.vendors = vendorCounts
    filterCounts.withinSubsidy = withinSubsidyCount
    filterCounts.withinSubsidyAndCredits = withinSubsidyAndCreditsCount

    return filterCounts
  }, [
    menuDataV2,
    previousMenuDataV2,
    menuFilters.vendors,
    menuFilters.allergens,
    budgetFilter,
    unlimitedSubsidy,
    balance,
    menuData?.availableSubsidy,
  ])

  useEffect(() => {
    if (dateISOString === null || timeslotISOString === null) return
    const dietaries = dietariesFiltersString.length ? dietariesFiltersString.split(',') : []
    const allergens = allergensFiltersString.length ? allergensFiltersString.split(',') : []
    const vendors = vendorFiltersString.length ? vendorFiltersString.split(',') : []

    runQuery({
      variables: {
        meal: meal as Meal,
        date: dateISOString,
        timeslotStart: timeslotISOString,
        mainsFilters: {
          menuItemType: ['main'],
          dietaries,
          allergens,
        },
        sidesFilters: {
          menuItemType: ['side'],
          dietaries,
          allergens,
        },
        drinksFilters: {
          menuItemType: ['drink'],
          dietaries,
          allergens,
        },
        vendorFilters: {
          ...(vendors.length && { includeVendorsById: vendors }),
        },
      },
      fetchPolicy: 'cache-and-network',
    })
  }, [
    dateISOString,
    meal,
    timeslotISOString,
    dietariesFiltersString,
    allergensFiltersString,
    vendorFiltersString,
    runQuery,
  ])

  const [orderAttemptQty, setOrderAttemptQty] = useState(0)
  const [showCancelBasketModal, setShowCancelBasketModal] = useState(false)
  const [showSideBar, setShowSideBar] = useState(false)
  const [errorMessageClosed, setErrorMessageClosed] = useState(false)
  const [basketEmptyConfirmationCallback, setBasketEmptyConfirmationCallback] = useState<
    (() => void) | null
  >(null)
  const [shouldShowFilters, setShouldShowFilters] = useState(true)

  useEffect(() => {
    if (!menuDataV2) setShouldShowFilters(true)
    // filters should not be shown if the menu is not open for orders yet
    setShouldShowFilters(!menuDataV2?.me.ccMenu?.mealServiceData.timeslots[0]?.isPreOrdersOpenTime)
  }, [menuDataV2])

  useEffect(() => {
    if (cartItemQuantity === 0 && showSideBar) {
      setShowSideBar(false)
    }
  }, [cartItemQuantity, showSideBar])

  useEffect(() => {
    if (cartItemQuantity === 0) return
    if (timeslot && menuClosed) emptyCart()
  }, [menuClosed, timeslot, emptyCart, cartItemQuantity])

  const handlePlaceOrder = async (bundle: number) => {
    if (!timeslot || menuClosed) {
      return
    }
    await placeOrder({
      language,
      bundle,
      items: cartItems,
      arrivalTime: timeslot,
      cartTotal,
      currency,
      balance: balance ?? 0,
      subsidy: menuData?.availableSubsidy ?? 0,
      unlimitedSubsidy: Boolean(userData.subsidySettings.unlimitedSubsidy),
      validDate: date,
      validMeal: meal,
      timezone: userData.accountLocation?.timezone,
    })
  }

  const forwardedOrderRef = useRef<string | null>(null)

  useEffect(() => {
    if (!placeOrderData) return
    if (forwardedOrderRef.current === placeOrderData) return
    forwardedOrderRef.current = placeOrderData
    onOrderPlacement({ success: true, forwardUrl: placeOrderData })
    return () => {
      resetPlaceOrder()
      emptyCart()
      setShowSideBar(false)
    }
  }, [placeOrderData, onOrderPlacement, resetPlaceOrder, emptyCart])

  useEffect(() => {
    if (!placeOrderError) return
    setOrderAttemptQty((prev) => prev + 1)
    setErrorMessageClosed(false)
    onOrderPlacement({ success: false, forwardUrl: '' })
  }, [placeOrderError, onOrderPlacement])

  const onNavChange = (callback: () => void) => {
    if (cartItems && cartItems.length > 0) {
      setShowCancelBasketModal(true)
      setBasketEmptyConfirmationCallback(() => callback)
    } else {
      callback()
    }
  }

  const cancelBasketModalClicked = () => {
    setShowCancelBasketModal(false)
    if (basketEmptyConfirmationCallback) {
      emptyCart()
      basketEmptyConfirmationCallback()
    }
  }

  const cancelBasketModalClosed = () => {
    setShowCancelBasketModal(false)
  }

  const handleShowSideBar = () => {
    setShowSideBar(true)
  }

  const closeSideBar = () => {
    if (placingOrder) return
    setShowSideBar(false)
  }

  const handleCloseOrderErrorMessage = () => {
    setErrorMessageClosed(true)
  }

  return (
    <>
      <Dialog onClose={() => cancelBasketModalClosed()} open={showCancelBasketModal}>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Info color="warning" />
            <Typography>Changing day or meal will empty your cart</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack paddingY={1}>
            <Typography>
              You can’t order for multiple days or meals at a time. If you want to change, your cart
              will be emptied. If that’s ok, empty your cart now.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => cancelBasketModalClosed()}>
            Continue ordering
          </Button>
          <Button
            onClick={() => {
              cancelBasketModalClicked()
            }}>
            Empty my cart
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(!errorMessageClosed && placeOrderError)}
        onClose={handleCloseOrderErrorMessage}>
        <Alert
          onClose={handleCloseOrderErrorMessage}
          severity="error"
          variant="filled"
          sx={{ width: '100%' }}>
          {placeOrderError}
        </Alert>
      </Snackbar>

      {menuData && timeslot ? (
        <MenuCartV2
          placingOrder={Boolean(placingOrder || placeOrderData)}
          currency={currency}
          onRemove={removeCartItem}
          open={showSideBar}
          userData={userData}
          menuData={menuData}
          cartItems={cartItems}
          cartTotal={cartTotal}
          onClose={closeSideBar}
          onUpdate={updateCartItem}
          placeOrder={handlePlaceOrder}
          onChangeTimeslot={onChangeTimeslot}
          arrivalTime={timeslot}
          orderTimeRemaining={orderTimeRemaining}
        />
      ) : null}

      {!ccItemRatingEnabled && <ItemRatingV2 />}
      <Stack sx={{ height: '100vh', overflow: 'hidden' }}>
        <Navbar
          onCartPreviewClick={() => handleShowSideBar()}
          userData={userData}
          cartTotal={cartTotal}
          cartItemQuantity={cartItemQuantity}
          useNewCcUi={true}
        />
        <Stack direction="row" sx={{ overflow: 'hidden', height: '100%' }}>
          {!isMobile ? (
            <FiltersPanelMuiV2
              onClearFilters={onClearFilters}
              searchTerm={searchTerm}
              onSearchChange={onSearchTermChange}
              menuFilters={menuFilters}
              onToggleFilterValue={onToggleFilterValue}
              budgetFilter={budgetFilter}
              onToggleBudgetFilter={onToggleBudgetFilter}
              loading={loadingV2}
              menuData={
                menuDataV2?.me.ccMenu?.mealServiceData.timeslots[0]?.allVendorMenus ||
                previousMenuDataV2?.me.ccMenu?.mealServiceData.timeslots[0]?.allVendorMenus
              }
              filteredMenuFilterCount={filteredMenuFilterCount}
              shouldShowFilters={shouldShowFilters}
              hideCCPrices={hidePrices}
              appPromo={appPromo}
            />
          ) : null}

          <Paper
            sx={{
              p: 2,
              backgroundColor: '#F4F4F4',
              flex: 1,
              overflowY: 'scroll',
              borderRadius: 0,
            }}>
            <CanteenRatingBanner userId={userId} sx={{ mt: 0, mb: 2 }} />
            <MenuHeaderV2
              onChangeTimeslot={onChangeTimeslot}
              onChangeAccountLocation={(accountLocationId) => {
                onNavChange(() => {
                  onChangeAccountLocation(accountLocationId)
                })
              }}
              onChangeSelectedService={(input) => {
                onNavChange(() => {
                  onChangeSelectedService(input)
                })
              }}
              userData={userData}
              menuData={menuData}
              meal={meal}
              isClosed={menuClosed}
              date={date}
              arrivalTime={timeslot}
              orderTimeRemaining={orderTimeRemaining}
              shouldShowMenusByList={shouldShowFilters}
            />
            {timeslot ? (
              <MenuBody
                filteredMenuFilterCount={filteredMenuFilterCount}
                ordersAttempted={orderAttemptQty}
                onClearFilters={onClearFilters}
                menuFilters={menuFilters}
                onToggleFilterValue={onToggleFilterValue}
                addToCart={(item) => {
                  if (placingOrder) return
                  addCartItem(item, timeslot)
                  handleShowSideBar()
                }}
                cartItems={cartItems}
                onSuccessfulCancellation={reloadFromApi}
                hidePrices={hidePrices}
                date={date}
                meal={meal as Meal}
                timeslot={timeslot}
                timezone={timezone}
                useNewCcUi={true}
                searchTerm={searchTerm}
                onSearchTermChange={onSearchTermChange}
                budgetFilter={budgetFilter}
                onToggleBudgetFilter={onToggleBudgetFilter}
                balance={balance}
                availableSubsidy={menuData?.availableSubsidy ?? 0}
                unlimitedSubsidy={unlimitedSubsidy}
                shouldShowFilters={shouldShowFilters}
              />
            ) : (
              <>
                {loadingSelectedServiceData ? null : (
                  <Stack
                    paddingTop={2}
                    direction="column"
                    spacing={2}
                    width="100%"
                    alignItems="center">
                    <HourglassTop color="info" sx={{ height: 60, width: 60 }} />
                    <Typography variant="h5">
                      There seems to be no menu data for this service.
                    </Typography>
                    <Typography variant="body1">Things may not be set up quite yet.</Typography>
                  </Stack>
                )}
              </>
            )}
          </Paper>
        </Stack>
      </Stack>
    </>
  )
}
